<template>
  <div v-if="visible" :class="['danger-sign-bar', `danger-sign-bar--${urgency}`]">
    <div class="danger-sign-bar__message">
      <v-icon class="danger-sign-bar__alert-icon" aria-hidden="true" size="22">
        {{ icons.mdiAlertOutline }}
      </v-icon>
      <span id="danger-sign-bar-message" class="danger-sign-bar__text">
        {{ message }}
      </span>
    </div>
    <button
      type="button"
      class="danger-sign-bar__toggle"
      :aria-label="$t(toggleToken)"
      :aria-expanded="String(expanded)"
      aria-controls="danger-sign-bar-findings"
      aria-describedby="danger-sign-bar-message"
      @click="expanded = !expanded"
    >
      <v-icon aria-hidden="true" size="22">
        {{ expanded ? mdiChevronUp : icons.mdiChevronDown }}
      </v-icon>
    </button>
    <button
      v-if="showStop"
      type="button"
      class="danger-sign-bar__stop"
      :aria-describedby="stopHintVisible ? 'danger-sign-bar-stop-hint' : null"
      @click="handleStop"
    >
      <v-icon class="danger-sign-bar__stop-icon" size="20">
        {{ icons.mdiStopCircleOutline }}
      </v-icon>
      {{ $t('app.danger_sign_alert.stop') }}
    </button>
    <p v-if="stopHintVisible" id="danger-sign-bar-stop-hint" class="danger-sign-bar__stop-hint" role="alert">
      {{ $t('app.danger_sign_alert.stop_needs_patient_id') }}
    </p>
    <ul v-show="expanded" id="danger-sign-bar-findings" class="danger-sign-bar__findings">
      <li v-for="sign in findings" :key="sign.name" class="danger-sign-bar__finding">
        <span :class="['danger-sign-bar__badge', `danger-sign-bar__badge--${urgencyOf(sign)}`]">
          {{ $t(`triage.${urgencyOf(sign)}.title`) }}
        </span>
        <span class="danger-sign-bar__name">
          {{ $t(nameToken(sign)) }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import { mdiChevronUp } from '@mdi/js'
import icons from '@/mixins/icons'
import { useOverallAssessmentStore } from '@/features/overall-assessment/stores/overallAssessment'
import { useDangerSignStore } from '../stores/dangerSign.store'
import { highestUrgency, nameToken, urgencyOf } from '../utils/dangerSignPresentation'

export default defineComponent({
  name: 'DangerSignBar',
  mixins: [icons],
  props: {
    showStop: { type: Boolean, default: true },
    stopBlocked: { type: Boolean, default: false },
  },
  setup(props) {
    const store = useDangerSignStore()
    const overallAssessmentStore = useOverallAssessmentStore()
    const expanded = ref(false)
    const stopHintShown = ref(false)
    const stopHintVisible = computed(() => stopHintShown.value && props.stopBlocked)
    const findings = computed(() => Object.values(store.findings))
    const urgency = computed(() => highestUrgency(findings.value))
    const toggleToken = computed(() => `app.assessment_mode.text.input.${expanded.value
      ? 'collapse_section' : 'expand_section'}`)

    const visible = computed(() => store.hasFindings
      && !store.hasPendingAlert
      && overallAssessmentStore.stage !== 'complete')

    watch(visible, isVisible => {
      if (!isVisible) expanded.value = false
    })
    watch(() => props.stopBlocked, blocked => {
      if (!blocked) stopHintShown.value = false
    })

    const handleStop = (): void => {
      if (props.stopBlocked) {
        stopHintShown.value = true
        return
      }
      store.requestStop()
    }

    return {
      visible,
      findings,
      urgency,
      expanded,
      toggleToken,
      mdiChevronUp,
      urgencyOf,
      nameToken,
      stopHintVisible,
      handleStop,
    }
  },
  computed: {
    message(): string {
      if (!this.urgency) return ''
      return String(this.$t(`app.danger_sign_persistent_alert.${this.urgency}`))
    },
  },
})
</script>

<style scoped src="../styles/danger-sign-bar.css"></style>
